import { create } from 'zustand'
import { shallow } from 'zustand/shallow'
import { convex, convexClient } from '../convexClient'
import { api } from '../lib/convexHelpers'
import toast from 'react-hot-toast'

// Import other stores for cross-store communication
import useQueueStore from './queueStore'
import usePlaybackStore from './playbackStore'
import useEmojiStore from './emojiStore'
import useProcessingStore from './processingStore'
import useSearchStore from './searchStore'

/**
 * Room store - handles room creation, validation, and state
 *
 * Real-time subscriptions are handled by useConvexSync hook (not this store).
 * This store manages room identity and provides actions for room lifecycle.
 */
const useRoomStore = create((set, get) => ({
  // State
  room: null,
  roomCode: null,
  roomId: null,
  isConnected: false,
  connectionStatus: 'disconnected', // disconnected, connecting, connected, error

  // Actions

  /**
   * Create a new room
   */
  createRoom: async () => {
    try {
      const result = await convex.mutation(api.rooms.create)
      const resolvedRoomId = result?._id ?? result?.id ?? null
      set({ roomCode: result.code, roomId: resolvedRoomId })
      return { code: result.code, id: resolvedRoomId }
    } catch (error) {
      toast.error('Failed to create room')
      throw error
    }
  },

  /**
   * Validate if a room exists
   */
  validateRoom: async (roomCode) => {
    try {
      const room = await convexClient.query(api.rooms.getByCode, { code: roomCode })
      if (room) {
        return { exists: true, room }
      }
      return { exists: false, error: 'Room not found' }
    } catch {
      return { exists: false, error: 'Unable to validate room' }
    }
  },

  /**
   * Join an existing room - sets room code for useConvexSync to pick up
   */
  joinRoom: async (roomCode) => {
    set({ roomCode, connectionStatus: 'connecting' })
  },

  /**
   * Connect to real-time subscriptions (no-op - handled by useConvexSync)
   */
  connectRealtime: async () => {
    // No-op: Convex reactive queries in useConvexSync handle real-time subscriptions
  },

  /**
   * Disconnect from real-time subscriptions
   */
  disconnectRealtime: () => {
    set({ isConnected: false, connectionStatus: 'disconnected' })
    useEmojiStore.getState().clearEmojiEvents()
  },

  /**
   * Refresh room state (no-op - Convex auto-refreshes)
   */
  refreshRoomState: async () => {
    return true
  },

  /**
   * Manual refresh
   */
  manualRefresh: async () => {
    toast.success('Room state is automatically synced')
  },

  /**
   * Force reconnection (no-op - Convex handles reconnection)
   */
  forceReconnection: async () => {
    toast.success('Connection is managed automatically')
  },

  getConnectionHealth: () => ({
    totalIntended: 1,
    totalActive: 1,
    healthy: 1,
    stale: 0,
    isReconnecting: false,
    autoReconnectEnabled: true,
  }),

  enableAutoReconnect: () => {},
  disableAutoReconnect: () => {},
  isAutoReconnectEnabled: () => true,

  getDebugInfo: () => ({
    storeState: {
      isConnected: get().isConnected,
      connectionStatus: get().connectionStatus,
      roomCode: get().roomCode,
      roomId: get().roomId,
    },
    transport: 'convex',
  }),

  /**
   * Reset all stores
   */
  reset: () => {
    set({
      room: null,
      roomCode: null,
      roomId: null,
      isConnected: false,
      connectionStatus: 'disconnected',
    })

    useQueueStore.getState().reset()
    usePlaybackStore.getState().reset()
    useEmojiStore.getState().clearEmojiEvents()
    useProcessingStore.getState().reset()
    useSearchStore.getState().reset()
  },
}))

// Selector hooks for granular subscriptions

export const useRoomConnection = () => useRoomStore(
  state => ({
    isConnected: state.isConnected,
    connectionStatus: state.connectionStatus
  }),
  shallow
)

export const useRoomInfo = () => useRoomStore(
  state => ({
    room: state.room,
    roomCode: state.roomCode,
    roomId: state.roomId
  }),
  shallow
)

export default useRoomStore
